import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { isAxiosError } from 'axios'
import { Lock, Unlock, ShieldAlert, CheckCircle2, XCircle, Clock } from 'lucide-react'
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  Select,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from '@/components/ui'
import { useAuthStore } from '@/stores/auth.ts'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ModuleLockStatus {
  key: string
  label: string
  locked: boolean
  lockedByUserId: string | null
  lockedAt: string | null
  lockReason: string | null
}

interface ChangeRequest {
  id: string
  module_key: string
  requested_by_user_id: string
  change_type: 'feature' | 'bugfix'
  reason: string
  issue_reference: string | null
  status: 'pending' | 'approved' | 'rejected'
  approved_by_user_id: string | null
  approved_at: string | null
  decision_note: string | null
  bugfix_veto_deadline: string | null
  created_at: string
  requester_name?: string
  requester_email?: string
  approver_name?: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function errMsg(e: unknown): string {
  if (isAxiosError(e)) {
    const d = e.response?.data as { error?: string; message?: string } | undefined
    return d?.error ?? d?.message ?? e.message
  }
  if (e instanceof Error) return e.message
  return String(e)
}

function formatDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
}

// ─── Change-Request Modal ─────────────────────────────────────────────────────

interface ChangeRequestModalProps {
  module: ModuleLockStatus | null
  onClose: () => void
}

function ChangeRequestModal({ module, onClose }: ChangeRequestModalProps) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [changeType, setChangeType] = useState<'feature' | 'bugfix'>('bugfix')
  const [reason, setReason] = useState('')
  const [issueRef, setIssueRef] = useState('')

  const mut = useMutation({
    mutationFn: () =>
      apiClient.post(`/module-locks/${module!.key}/change-request`, {
        changeType,
        reason,
        issueReference: issueRef || undefined,
      }),
    onSuccess: () => {
      toast({ title: 'Antrag gestellt', description: 'Der Änderungsantrag wurde eingereicht.', tone: 'success' })
      qc.invalidateQueries({ queryKey: ['module-locks-my-requests'] })
      onClose()
    },
    onError: (e) => toast({ title: 'Fehler', description: errMsg(e), tone: 'danger' }),
  })

  return (
    <Modal
      open={!!module}
      onClose={onClose}
      title={`Änderungsantrag: ${module?.label}`}
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            onClick={() => mut.mutate()}
            disabled={reason.trim().length < 5 || mut.isPending}
          >
            {mut.isPending ? 'Wird gesendet…' : 'Antrag stellen'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-body text-gray-600">
          Das Modul <strong>{module?.label}</strong> ist gesperrt.
          Bitte begründen Sie Ihre Änderungsabsicht.
        </p>
        <Select
          label="Änderungstyp"
          value={changeType}
          onValueChange={(v) => setChangeType(v as 'feature' | 'bugfix')}
          options={[
            { value: 'bugfix', label: 'Bug-Fix (24h Veto-Fenster)' },
            { value: 'feature', label: 'Feature-Änderung (aktive Freigabe nötig)' },
          ]}
        />
        <Textarea
          label="Begründung"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          placeholder="Was soll geändert werden und warum ist es notwendig?"
          hint={changeType === 'bugfix'
            ? 'Bug-Fixes werden automatisch nach 24h genehmigt, wenn kein Widerspruch erfolgt.'
            : 'Feature-Änderungen benötigen eine aktive Genehmigung durch Admin/GF.'}
        />
        <Input
          label="Ticket-Referenz (optional)"
          value={issueRef}
          onChange={(e) => setIssueRef(e.target.value)}
          placeholder="z.B. CODA-1234 oder GitHub-Issue-URL"
        />
      </div>
    </Modal>
  )
}

// ─── Lock Modal ───────────────────────────────────────────────────────────────

interface LockModalProps {
  module: ModuleLockStatus | null
  onClose: () => void
}

function LockModal({ module, onClose }: LockModalProps) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [reason, setReason] = useState('')

  const mut = useMutation({
    mutationFn: () => apiClient.post(`/module-locks/${module!.key}/lock`, { reason }),
    onSuccess: () => {
      toast({ title: 'Modul gesperrt', description: `${module?.label} ist jetzt gesperrt.`, tone: 'success' })
      qc.invalidateQueries({ queryKey: ['module-locks'] })
      onClose()
    },
    onError: (e) => toast({ title: 'Fehler', description: errMsg(e), tone: 'danger' }),
  })

  return (
    <Modal
      open={!!module}
      onClose={onClose}
      title={`Modul sperren: ${module?.label}`}
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button
            variant="danger"
            onClick={() => mut.mutate()}
            disabled={reason.trim().length < 3 || mut.isPending}
          >
            <Lock className="w-4 h-4" />
            {mut.isPending ? 'Wird gesperrt…' : 'Sperren'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-body text-gray-600">
          Das Modul <strong>{module?.label}</strong> wird gesperrt.
          Alle Änderungen an diesem Modul benötigen anschließend eine Genehmigung.
        </p>
        <Textarea
          label="Sperrgrund"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="Warum wird das Modul gesperrt?"
        />
      </div>
    </Modal>
  )
}

// ─── Module List Tab ──────────────────────────────────────────────────────────

interface ModuleListTabProps {
  canManage: boolean
}

function ModuleListTab({ canManage }: ModuleListTabProps) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [lockTarget, setLockTarget] = useState<ModuleLockStatus | null>(null)
  const [requestTarget, setRequestTarget] = useState<ModuleLockStatus | null>(null)
  const [unlockConfirm, setUnlockConfirm] = useState<ModuleLockStatus | null>(null)

  const { data, isLoading, error } = useQuery<ModuleLockStatus[]>({
    queryKey: ['module-locks'],
    queryFn: () => apiClient.get('/module-locks').then((r) => r.data),
  })

  const unlockMut = useMutation({
    mutationFn: (key: string) => apiClient.post(`/module-locks/${key}/unlock`, {}),
    onSuccess: (_, key) => {
      const label = data?.find((m) => m.key === key)?.label ?? key
      toast({ title: 'Modul entsperrt', description: `${label} ist jetzt entsperrt.`, tone: 'success' })
      qc.invalidateQueries({ queryKey: ['module-locks'] })
    },
    onError: (e) => toast({ title: 'Fehler', description: errMsg(e), tone: 'danger' }),
  })

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState title="Fehler beim Laden" description={errMsg(error)} />

  return (
    <>
      <div className="space-y-3">
        {(data ?? []).map((mod) => (
          <Card key={mod.key}>
            <Card.Body>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  {mod.locked
                    ? <Lock className="w-5 h-5 text-red-500 shrink-0" />
                    : <Unlock className="w-5 h-5 text-green-500 shrink-0" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-body">{mod.label}</span>
                      <StatusBadge status={mod.locked ? 'locked' : 'unlocked'} />
                    </div>
                    {mod.locked && (
                      <p className="text-caption text-gray-500 mt-0.5">
                        Gesperrt {mod.lockedAt ? `am ${formatDate(mod.lockedAt)}` : ''}{' '}
                        {mod.lockReason ? `— ${mod.lockReason}` : ''}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  {mod.locked && !canManage && (
                    <Button variant="secondary" size="sm" onClick={() => setRequestTarget(mod)}>
                      <ShieldAlert className="w-4 h-4" />
                      Änderungsantrag
                    </Button>
                  )}
                  {canManage && mod.locked && (
                    <>
                      <Button variant="secondary" size="sm" onClick={() => setRequestTarget(mod)}>
                        <ShieldAlert className="w-4 h-4" />
                        Antrag stellen
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUnlockConfirm(mod)}
                        disabled={unlockMut.isPending}
                      >
                        <Unlock className="w-4 h-4" />
                        Entsperren
                      </Button>
                    </>
                  )}
                  {canManage && !mod.locked && (
                    <Button variant="danger" size="sm" onClick={() => setLockTarget(mod)}>
                      <Lock className="w-4 h-4" />
                      Sperren
                    </Button>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>

      <LockModal module={lockTarget} onClose={() => setLockTarget(null)} />
      <ChangeRequestModal module={requestTarget} onClose={() => setRequestTarget(null)} />

      <ConfirmDialog
        open={!!unlockConfirm}
        title={`${unlockConfirm?.label} entsperren?`}
        message="Das Modul wird entsperrt. Änderungen benötigen keine Genehmigung mehr."
        confirmLabel="Entsperren"
        onConfirm={() => {
          if (unlockConfirm) unlockMut.mutate(unlockConfirm.key)
          setUnlockConfirm(null)
        }}
        onCancel={() => setUnlockConfirm(null)}
      />
    </>
  )
}

// ─── Approval Queue Tab ───────────────────────────────────────────────────────

interface DecisionModalProps {
  request: ChangeRequest | null
  onClose: () => void
}

function DecisionModal({ request, onClose }: DecisionModalProps) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [decision, setDecision] = useState<'approved' | 'rejected'>('approved')
  const [note, setNote] = useState('')

  const mut = useMutation({
    mutationFn: () =>
      apiClient.patch(`/module-locks/change-requests/${request!.id}`, {
        status: decision,
        decisionNote: note || undefined,
      }),
    onSuccess: () => {
      toast({
        title: decision === 'approved' ? 'Genehmigt' : 'Abgelehnt',
        description: `Antrag wurde ${decision === 'approved' ? 'genehmigt' : 'abgelehnt'}.`,
        tone: decision === 'approved' ? 'success' : 'warning',
      })
      qc.invalidateQueries({ queryKey: ['module-locks-requests'] })
      onClose()
    },
    onError: (e) => toast({ title: 'Fehler', description: errMsg(e), tone: 'danger' }),
  })

  const MODULE_LABELS: Record<string, string> = {
    tanss: 'TANSS-Integration', sap: 'SAP-Integration', liquidity: 'Liquiditätsplanung',
    dashboard: 'Dashboard', cwa: 'CWA', eset: 'ESET', also: 'ALSO', egis: 'EGIS',
  }

  return (
    <Modal
      open={!!request}
      onClose={onClose}
      title="Antrag entscheiden"
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
          <Button
            variant={decision === 'approved' ? 'primary' : 'danger'}
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
          >
            {decision === 'approved'
              ? <><CheckCircle2 className="w-4 h-4" /> Genehmigen</>
              : <><XCircle className="w-4 h-4" /> Ablehnen</>}
          </Button>
        </div>
      }
    >
      {request && (
        <div className="space-y-4">
          <div className="rounded-lg bg-gray-50 p-3 space-y-2 text-body">
            <div><span className="text-gray-500">Modul:</span> <strong>{MODULE_LABELS[request.module_key] ?? request.module_key}</strong></div>
            <div><span className="text-gray-500">Typ:</span> {request.change_type === 'bugfix' ? 'Bug-Fix' : 'Feature-Änderung'}</div>
            <div><span className="text-gray-500">Von:</span> {request.requester_name ?? request.requested_by_user_id}</div>
            {request.bugfix_veto_deadline && (
              <div className="flex items-center gap-1 text-amber-600">
                <Clock className="w-4 h-4" />
                <span>Veto-Frist: {formatDate(request.bugfix_veto_deadline)}</span>
              </div>
            )}
            <div><span className="text-gray-500">Begründung:</span> {request.reason}</div>
            {request.issue_reference && (
              <div><span className="text-gray-500">Ticket:</span> {request.issue_reference}</div>
            )}
          </div>
          <Select
            label="Entscheidung"
            value={decision}
            onValueChange={(v) => setDecision(v as 'approved' | 'rejected')}
            options={[
              { value: 'approved', label: 'Genehmigen' },
              { value: 'rejected', label: 'Ablehnen' },
            ]}
          />
          <Textarea
            label="Notiz (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Begründung für die Entscheidung…"
          />
        </div>
      )}
    </Modal>
  )
}

function ApprovalQueueTab() {
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [decisionTarget, setDecisionTarget] = useState<ChangeRequest | null>(null)

  const { data, isLoading, error } = useQuery<ChangeRequest[]>({
    queryKey: ['module-locks-requests', statusFilter],
    queryFn: () => apiClient.get(`/module-locks/change-requests?status=${statusFilter}`).then((r) => r.data),
  })

  const MODULE_LABELS: Record<string, string> = {
    tanss: 'TANSS', sap: 'SAP', liquidity: 'Liquiditätsplanung',
    dashboard: 'Dashboard', cwa: 'CWA', eset: 'ESET', also: 'ALSO', egis: 'EGIS',
  }

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState title="Fehler beim Laden" description={errMsg(error)} />

  return (
    <>
      <div className="mb-4 flex gap-3 items-center">
        <Select
          value={statusFilter}
          onValueChange={setStatusFilter}
          options={[
            { value: 'pending', label: 'Offen' },
            { value: 'approved', label: 'Genehmigt' },
            { value: 'rejected', label: 'Abgelehnt' },
          ]}
        />
      </div>

      {data?.length === 0 && (
        <EmptyState
          title="Keine Anträge"
          description={statusFilter === 'pending' ? 'Derzeit liegen keine offenen Änderungsanträge vor.' : 'Keine Einträge für diesen Status.'}
          icon={<CheckCircle2 />}
        />
      )}

      <div className="space-y-3">
        {(data ?? []).map((req) => (
          <Card key={req.id}>
            <Card.Body>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-body">{MODULE_LABELS[req.module_key] ?? req.module_key}</span>
                    <StatusBadge status={req.change_type === 'bugfix' ? 'bugfix' : 'feature'} />
                    <StatusBadge status={req.status} />
                    {req.bugfix_veto_deadline && req.status === 'pending' && (
                      <span className="flex items-center gap-1 text-amber-600 text-caption">
                        <Clock className="w-3 h-3" />
                        Veto bis {formatDate(req.bugfix_veto_deadline)}
                      </span>
                    )}
                  </div>
                  <p className="text-body text-gray-700 line-clamp-2">{req.reason}</p>
                  <p className="text-caption text-gray-500">
                    Von {req.requester_name ?? req.requested_by_user_id} · {formatDate(req.created_at)}
                    {req.issue_reference && ` · Ticket: ${req.issue_reference}`}
                  </p>
                  {req.status !== 'pending' && req.decision_note && (
                    <p className="text-caption text-gray-600">Notiz: {req.decision_note}</p>
                  )}
                </div>
                {req.status === 'pending' && (
                  <Button variant="primary" size="sm" onClick={() => setDecisionTarget(req)}>
                    Entscheiden
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>

      <DecisionModal request={decisionTarget} onClose={() => setDecisionTarget(null)} />
    </>
  )
}

// ─── My Requests Tab ──────────────────────────────────────────────────────────

function MyRequestsTab() {
  const { data, isLoading, error } = useQuery<ChangeRequest[]>({
    queryKey: ['module-locks-my-requests'],
    queryFn: () => apiClient.get('/module-locks/my-requests').then((r) => r.data),
  })

  const MODULE_LABELS: Record<string, string> = {
    tanss: 'TANSS', sap: 'SAP', liquidity: 'Liquiditätsplanung',
    dashboard: 'Dashboard', cwa: 'CWA', eset: 'ESET', also: 'ALSO', egis: 'EGIS',
  }

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState title="Fehler beim Laden" description={errMsg(error)} />

  if (!data?.length) {
    return (
      <EmptyState
        title="Keine Anträge"
        description="Sie haben noch keine Änderungsanträge gestellt."
        icon={<ShieldAlert />}
      />
    )
  }

  return (
    <div className="space-y-3">
      {data.map((req) => (
        <Card key={req.id}>
          <Card.Body>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-body">{MODULE_LABELS[req.module_key] ?? req.module_key}</span>
                <StatusBadge status={req.change_type === 'bugfix' ? 'bugfix' : 'feature'} />
                <StatusBadge status={req.status} />
              </div>
              <p className="text-body text-gray-700 line-clamp-2">{req.reason}</p>
              <p className="text-caption text-gray-500">
                Gestellt {formatDate(req.created_at)}
                {req.approved_at && ` · Entschieden ${formatDate(req.approved_at)} von ${req.approver_name ?? '—'}`}
              </p>
              {req.decision_note && (
                <p className="text-caption text-gray-600">Notiz: {req.decision_note}</p>
              )}
              {req.bugfix_veto_deadline && req.status === 'pending' && (
                <p className="flex items-center gap-1 text-caption text-amber-600">
                  <Clock className="w-3 h-3" />
                  Automatisch genehmigt nach {formatDate(req.bugfix_veto_deadline)} (kein Veto)
                </p>
              )}
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ModuleLocksTab() {
  const currentUser = useAuthStore((s) => s.user)
  const canManage = currentUser?.role === 'admin' || currentUser?.role === 'gf'

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-h3 font-semibold">Modul-Sperren</h2>
        <p className="text-body text-gray-500 mt-1">
          Gesperrte Module erfordern eine Genehmigung für jede Änderung.
          Bug-Fixes gelten nach 24h automatisch als genehmigt (Veto-Fenster).
          {canManage ? ' Als Admin/GF können Sie Module sperren und entsperren.' : ''}
        </p>
      </div>

      <Tabs defaultValue="modules">
        <TabsList>
          <TabsTrigger value="modules">Module</TabsTrigger>
          <TabsTrigger value="my-requests">Meine Anträge</TabsTrigger>
          {canManage && <TabsTrigger value="approval-queue">Freigabe-Queue</TabsTrigger>}
        </TabsList>

        <TabsContent value="modules">
          <ModuleListTab canManage={canManage} />
        </TabsContent>

        <TabsContent value="my-requests">
          <MyRequestsTab />
        </TabsContent>

        {canManage && (
          <TabsContent value="approval-queue">
            <ApprovalQueueTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
